import { type Ref, ref } from "vue";
import { defineStore } from "pinia";
import type { IGQLBookingOverview, IGQLTripsOverview } from "@/models/graphql/BookingOverviewResponse";
import { SeatDirection } from "@/models/basket/ReservationPreferences";

export const useBookingData = defineStore(
    "bookingData",
    () => {
        const bookingData: Ref<IGQLBookingOverview | null> = ref(null);
        const selectedPaymentMethod: Ref<"card" | "onAccount"> = ref("card");
        // State to hold organisation details (fetched by UserPayment)
        const organisationDetails = ref(null);

        const seatDirectionDropdownOptions = [
            { title: "Any", value: SeatDirection.NoPreference },
            { title: "Forward", value: SeatDirection.ForwardFacingAirline },
            { title: "Forward with Table", value: SeatDirection.ForwardFacing },
            { title: "Backward", value: SeatDirection.BackwardFacingAirline },
            { title: "Backward with Table", value: SeatDirection.BackwardFacing },
        ];

        function clearBookingData() {
            bookingData.value = null;
            selectedPaymentMethod.value = "card";
            // Clear organisation details when booking data is cleared
            organisationDetails.value = null;
        }

        function checkExistingBooking(): string | null {
            let bookingId: string | null = null;
            if (bookingData.value !== null) {
                const bookingObject = bookingData.value;
                // Ensure bookingExpiration exists before creating a Date object
                if (bookingObject.bookingExpiration) {
                    const currentDate = new Date();
                    const expirationToken = new Date(bookingObject.bookingExpiration);
                    const isValid = expirationToken > currentDate;
                    if (isValid) {
                        bookingId = bookingObject.id;
                    } else {
                        // Booking expired, clear all related data
                        bookingId = null;
                        clearBookingData(); // Use the clear function
                    }
                } else {
                    // If no expiration, assume invalid or treat as error? For now, treat as invalid.
                    bookingId = null;
                    clearBookingData();
                }
            }
            return bookingId;
        }

        function getExistingBookingFee() {
            let bookingFee: number | null = null;
            if (bookingData.value !== null) {
                const bookingObject = bookingData.value;
                bookingFee = bookingObject.bookingFee as number;
            }
            return bookingFee;
        }

        function bookedTrips(): IGQLTripsOverview[] | null {
            if (bookingData.value !== null) {
                const bookingObject = bookingData.value;
                return bookingObject.trips;
            }
            return null;
        }

        // Set the payment method
        function setPaymentMethod(method: "card" | "onAccount") {
            selectedPaymentMethod.value = method;
        }

        // Action to set organisation details from UserPayment component
        function setOrganisationDetails(details: any) {
            organisationDetails.value = details;
        }

        function isOnAccountPaymentSelected() {
            return selectedPaymentMethod.value === "onAccount";
        }

        return {
            bookingData,
            selectedPaymentMethod,
            organisationDetails,
            seatDirectionDropdownOptions,

            // Actions & Getters
            checkExistingBooking,
            getExistingBookingFee,
            bookedTrips,
            clearBookingData,
            setPaymentMethod,
            setOrganisationDetails,
            isOnAccountPaymentSelected,
        };
    },
    {
        persist: [
            {
                storage: localStorage,
                // Only persist booking data and selected payment method
                // Organisation details will be fetched when needed
                pick: ["bookingData", "selectedPaymentMethod"],
            },
        ],
    }
);
